import { useCallback, useEffect, useMemo, useState } from 'react'
import type { SortingDirType } from '@/api/request'

export interface SortWithDirection {
  value: string
  dirLabel: string
  dir: SortingDirType
}

const DIRS: { dir: SortingDirType; label: string }[] = [
  { dir: 'ASC', label: '↑' },
  { dir: 'DESC', label: '↓' },
]

interface UseSortOptionsArgs {
  sortByOptions?: string[]
  sortBy?: string
  sortDir?: SortingDirType
}

export default function useSortOptions({
  sortByOptions,
  sortBy,
  sortDir,
}: UseSortOptionsArgs) {
  const calculate = useCallback((opts?: string[]) => {
    if (!opts?.length) return []
    return opts.flatMap(option =>
      DIRS.map(({ dir, label }) => ({
        value: option,
        dir,
        dirLabel: label,
      }))
    )
  }, [])

  const [sortOptions, setSortOptions] = useState<SortWithDirection[]>(() =>
    calculate(sortByOptions)
  )

  useEffect(() => {
    setSortOptions(calculate(sortByOptions))
  }, [sortByOptions, calculate])

  const selectedSortOption = useMemo(
    () =>
      sortOptions.find(
        o => o.value === sortBy && o.dir === sortDir
      ),
    [sortOptions, sortBy, sortDir]
  )

  return {
    sortOptions,
    selectedSortOption,
  }
}
